import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supportAPI } from '../../services/api';

const STATUS_CLASS = { open: 'bg-amber-100 text-amber-700', answered: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-200 text-slate-600' };

function TicketThread({ ticketId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  function load() {
    return supportAPI.myTicket(ticketId).then((r) => setData(r)).catch(() => setError('Failed to load.'));
  }
  useEffect(() => { load().finally(() => setLoading(false)); }, [ticketId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'nearest' }); }, [data?.messages?.length]);

  async function handleReply(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setError('');
    try {
      await supportAPI.myReply(ticketId, reply.trim());
      setReply('');
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send.');
    } finally {
      setSending(false);
    }
  }

  // Portaled to document.body — see the matching note in manager/Support.jsx's
  // TicketModal: Layout's backdrop-blur-xl content card otherwise traps this
  // `fixed` modal inside its own box instead of covering the full screen.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center">
      <div className="flex h-[85dvh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-xl sm:h-[70dvh] sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[data?.ticket?.status] || 'bg-slate-100'}`}>
            {data?.ticket?.status || '…'}
          </span>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
          ) : (
            <div className="space-y-3">
              {(data?.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.sender_type === 'admin' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-6 shadow-sm ${
                    m.sender_type === 'admin' ? 'bg-slate-100 text-slate-800' : 'bg-orange-500 text-white'
                  }`}>
                    <p className="whitespace-pre-wrap">{m.message}</p>
                    <p className={`mt-1 text-[10px] ${m.sender_type === 'admin' ? 'text-slate-400' : 'text-orange-100'}`}>
                      {m.sender_type === 'admin' ? 'AWTF Support' : 'You'} · {m.created_at?.slice(0, 16).replace('T', ' ')}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 p-3">
          {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
          {data?.ticket?.status === 'closed' ? (
            <p className="text-center text-xs text-slate-400">This ticket is closed.</p>
          ) : (
            <form onSubmit={handleReply} className="flex gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)}
                className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-[16px] sm:text-sm" placeholder="Type a message…" />
              <button type="submit" disabled={sending || !reply.trim()}
                className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {sending ? '…' : 'Send'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function NewTicketModal({ onClose, onCreated }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true); setError('');
    try {
      await supportAPI.createMine(message.trim(), subject.trim());
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send.');
      setBusy(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">Contact Support</h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {error && <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Subject (optional)</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">What's the issue?</label>
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" required />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={busy} className="rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function MemberSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  function load() {
    return supportAPI.myTickets().then((r) => setTickets(r.tickets || [])).catch(() => {});
  }
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Support</h2>
          <p className="mt-1 text-sm text-slate-600">Have an issue or question? Send it to AWTF and get a reply here.</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-500">
          + New
        </button>
      </div>

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : tickets.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-10 text-center shadow-sm">
          <p className="text-slate-500">No support requests yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => setOpenId(t.id)}
              className="flex w-full items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3.5 text-left shadow-sm hover:bg-slate-50">
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-800">{t.subject || 'Support request'}</p>
                <p className="text-xs text-slate-400">{t.last_message_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[t.status] || 'bg-slate-100'}`}>
                {t.status}
              </span>
            </button>
          ))}
        </div>
      )}

      {showNew && (
        <NewTicketModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
      {openId && (
        <TicketThread ticketId={openId} onClose={() => { setOpenId(null); load(); }} onChanged={load} />
      )}
    </section>
  );
}
